import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { DocumentDialogData } from '../services/interfaces';

@Component({
    selector: 'app-document-viewer-dialog',
    templateUrl: './document-viewer-dialog.component.html',
    styleUrls: ['./document-viewer-dialog.component.scss']
})
export class DocumentViewerDialogComponent implements OnInit
{
    safeUrl: SafeResourceUrl;
    isLoading: boolean = true;
    fileExtension: string;
    isPdf: boolean = false;
    isImage: boolean = false;
    isOfficeDoc: boolean = false;
    isText: boolean = false;
    errorMessage: string = '';
    watermarkText: string = '';
    private loadStartTime: number = 0;
    private minLoadingTime: number = 800; // Minimum time to show loading spinner (ms)

    constructor(
        public dialogRef: MatDialogRef<DocumentViewerDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: DocumentDialogData,
        private sanitizer: DomSanitizer
    )
    {
    }

    ngOnInit(): void
    {
        console.log('📄 Document Viewer Initializing...', this.data);
        this.loadStartTime = Date.now();
        this.detectFileType();
        this.loadDocument();
    }

    private detectFileType(): void
    {
        const url = this.data.fileUrl.toLowerCase();

        // Get file extension (handles URLs with query parameters like AWS signed URLs)
        const match = url.match(/\.([^.?#]+)(?:[?#]|$)/);
        this.fileExtension = match ? match[1] : '';

        // Detect file type
        this.isPdf = url.includes('.pdf') || this.fileExtension === 'pdf';
        this.isImage = /\.(jpg|jpeg|png|gif|bmp|svg|webp)/.test(url);
        this.isOfficeDoc = /\.(doc|docx|xls|xlsx|ppt|pptx)/.test(url);
        this.isText = /\.(txt|json|xml|csv)/.test(url);
    }

    private loadDocument(): void
    {
        try
        {
            let fileUrl: string;

            // Use URL exactly as provided, but remove any surrounding quotes
            fileUrl = this.data.fileUrl;

            // Remove leading and trailing quotes if present
            if (fileUrl)
            {
                fileUrl = fileUrl.trim();
                // Remove double quotes
                if (fileUrl.startsWith('"') && fileUrl.endsWith('"'))
                {
                    fileUrl = fileUrl.slice(1, -1);
                    console.log('⚠️ Removed surrounding double quotes from URL');
                }
                // Remove single quotes
                if (fileUrl.startsWith("'") && fileUrl.endsWith("'"))
                {
                    fileUrl = fileUrl.slice(1, -1);
                    console.log('⚠️ Removed surrounding single quotes from URL');
                }
            }
            // File type detection for display purposes only
            console.log('File type detected:', {
                isPdf: this.isPdf,
                isImage: this.isImage,
                isOfficeDoc: this.isOfficeDoc,
                isText: this.isText
            });

            // Pass URL to Angular sanitizer - this ONLY marks it as safe, does NOT modify the URL
            // The sanitizer.bypassSecurityTrustResourceUrl() function tells Angular:
            // "I trust this URL, use it exactly as-is in the iframe"
            if (this.isPdf)
            {
                // For PDFs, add parameters to disable download and printing
                // Check if URL already has query parameters (AWS signed URLs)
                const separator = fileUrl.includes('?') ? '#' : '#';
                const pdfUrl = fileUrl + separator + 'navpanes=0&scrollbar=0&view=FitH';

                this.safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(pdfUrl);
                console.log('✅ PDF viewer initialized - download disabled via toolbar=0');
            } else if (this.isImage)
            {
                this.safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(fileUrl);
                console.log('✅ Image viewer initialized - URL passed unchanged');
            } else if (this.isOfficeDoc)
            {
                // Office documents need to be wrapped in Microsoft viewer
                const encodedUrl = encodeURIComponent(fileUrl);
                this.safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
                    `https://view.officeapps.live.com/op/embed.aspx?src=${encodedUrl}`
                );
                console.log('✅ Office viewer initialized');
            } else if (this.isText)
            {
                this.safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(fileUrl);
                console.log('✅ Text viewer initialized - URL passed unchanged');
            } else
            {
                // Unknown file types use Google Docs viewer
                const encodedUrl = encodeURIComponent(fileUrl);
                this.safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
                    `https://docs.google.com/gview?url=${encodedUrl}&embedded=true`
                );
                console.log('✅ Google Docs viewer initialized as fallback');
            }

            console.log('=== Document Viewer Ready ===');
            console.log('Final iframe src will be:', fileUrl);

            // Keep loading true, let iframe load event turn it off
            // Set a timeout as fallback in case load event doesn't fire
            setTimeout(() => {
                if (this.isLoading) {
                    console.log('⚠️ Iframe load event timeout - forcing loading off');
                    this.isLoading = false;
                }
            }, 5000);
        } catch (error)
        {
            console.error('Error loading document:', error);
            this.errorMessage = 'Failed to load document. Please try again or contact support.';
            this.isLoading = false;
        }
    }

    onClose(): void
    {
        this.dialogRef.close();
    }

    onIframeLoad(): void
    {
        const elapsedTime = Date.now() - this.loadStartTime;
        const remainingTime = Math.max(0, this.minLoadingTime - elapsedTime);

        console.log(`✅ Iframe loaded in ${elapsedTime}ms`);

        if (remainingTime > 0) {
            console.log(`⏱️ Extending loading display by ${remainingTime}ms for better UX`);
            setTimeout(() => {
                this.isLoading = false;
                console.log('🎉 Document viewer ready');
            }, remainingTime);
        } else {
            this.isLoading = false;
            console.log('🎉 Document viewer ready');
        }
    }

    onIframeError(): void
    {
        console.error('❌ Iframe load error');
        this.isLoading = false;
        this.errorMessage = 'Unable to display this file type.';
    }
}
